import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Table, Form, Button, Spinner } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import {
    updateMember,
    setSimpleDataToMemberData,
    getMemberHealthRecordList,
} from "app/redux/slices/rdxMemberSlice";
import Avatars from "./Avatars";
import "bootstrap/dist/css/bootstrap.min.css";
import "./membercss/MemberInfo.css";
import useTranslations from "../transalation/TranslationUtils";

// Health-record device types (maps to the API `device_type` field).
const DEVICE_OXIMETER = "oximeter";
const DEVICE_BLOOD_PRESSURE = "blood_pressure";

// updatememberbasic validates the whole basic-info record, so the existing
// member fields must be sent back alongside the biometrics (these mirror the
// keys BasicInformation submits). f_age is derived/ignored.
const BASIC_INFO_FIELDS = [
    "account_name", "account_birth", "account_gender", "account_phone",
    "member_id", "account_email", "card_ref", "lang", "account_address",
    "account_avatar",
];

const MONTH_LABEL_KEYS = [
    "monthJan", "monthFeb", "monthMar", "monthApr", "monthMay", "monthJun",
    "monthJul", "monthAug", "monthSep", "monthOct", "monthNov", "monthDec",
];

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function HealthProfile() {
    const dispatch = useDispatch();
    const memberEditData = useSelector((state) => state.rdxMember?.memberEditData || {});
    const accountId = memberEditData.account_id;

    const t = useTranslations();
    const {
        primaryBiometrics, height, weight,
        oxygenSaturation, bloodPressure, recentMeasurementLog,
        filterBy, year, date, time, oxygenSatAndPulse, bloodPressureAndPulse,
        noMeasurementData, savechaneg, showSuccessMsg, showErrMsg,
    } = t;

    // Biometric values (cm / kg). Sourced from the member record; may be null
    // for older accounts created before these fields existed.
    const [heightCm, setHeightCm] = useState("");
    const [weightKg, setWeightKg] = useState("");
    const [saving, setSaving] = useState(false);

    // Measurement log. The API filters server-side by device_type + month + year,
    // so the tab and the month/year selectors all drive a fresh query.
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(DEVICE_OXIMETER);
    const [filterMonth, setFilterMonth] = useState(() => new Date().getMonth()); // 0..11
    const [filterYear, setFilterYear] = useState(() => new Date().getFullYear());

    // Year dropdown: current year back to at most five years prior, but never
    // before 2026 (the first year health records exist).
    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        const start = Math.max(2026, current - 5);
        const list = [];
        for (let y = current; y >= start; y--) list.push(y);
        return list;
    }, []);

    // Biometrics live on the member record (updatememberbasic). Mirror them into
    // local state so the inputs are editable; null/undefined render as blank.
    useEffect(() => {
        setHeightCm(memberEditData.account_height ?? "");
        setWeightKg(memberEditData.account_weight ?? "");
    }, [accountId, memberEditData.account_height, memberEditData.account_weight]);

    // ---- Measurement log fetch -------------------------------------------
    useEffect(() => {
        if (!accountId) {
            setRecords([]);
            return;
        }
        let active = true;
        setLoading(true);
        dispatch(getMemberHealthRecordList(
            {
                account_id: accountId,
                device_type: activeTab,
                month: Number(filterMonth) + 1, // API months are 1-based
                year: Number(filterYear),
            },
            (isSuccess, data) => {
                if (!active) return; // a newer request superseded this one
                setLoading(false);
                if (isSuccess) {
                    setRecords(Array.isArray(data.data) ? data.data : []);
                } else {
                    setRecords([]);
                    alert(showErrMsg(data));
                }
            },
        ));
        return () => { active = false; };
    }, [accountId, activeTab, filterMonth, filterYear]);

    // ---- Save -------------------------------------------------------------
    const handleSave = (event) => {
        event.preventDefault();
        if (!accountId) return;
        setSaving(true);
        const biometrics = {
            account_height: heightCm === "" ? null : Number(heightCm),
            account_weight: weightKg === "" ? null : Number(weightKg),
        };
        // Resend the existing basic-info fields so the record passes validation.
        const sendData = { account_id: accountId, is_lock: memberEditData.is_lock };
        BASIC_INFO_FIELDS.forEach((key) => { sendData[key] = memberEditData[key]; });
        Object.assign(sendData, biometrics);
        dispatch(updateMember(sendData, (isSuccess, data) => {
            setSaving(false);
            if (isSuccess) {
                // Keep memberEditData in sync so the values persist across tabs.
                dispatch(setSimpleDataToMemberData({
                    data: biometrics,
                    projection: "self",
                    base: memberEditData,
                }));
                alert(showSuccessMsg(data));
            } else {
                alert(showErrMsg(data));
            }
        }));
    };

    // ---- Height / weight inputs ------------------------------------------
    const onHeightCm = (e) => setHeightCm(e.target.value);
    const onWeightKg = (e) => setWeightKg(e.target.value);

    // ---- Measurement log rendering ---------------------------------------
    const rows = useMemo(
        () => [...records].sort((a, b) => b.measure_timestamp - a.measure_timestamp),
        [records],
    );

    const formatDate = (ts) => {
        const d = new Date(ts);
        return `${d.getDate()}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
    };

    const formatTime = (ts) => {
        const d = new Date(ts);
        let h = d.getHours();
        const m = String(d.getMinutes()).padStart(2, "0");
        const ampm = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        return `${h}:${m} ${ampm}`;
    };

    const formatReading = (m) => {
        if (m.ocr_status === "FAILED") {
            return <span style={{ color: "#C0392B" }}>{m.error_message || "—"}</span>;
        }
        return m.device_type === DEVICE_OXIMETER
            ? `${m.spo2}%; ${m.pulse}bpm`
            : `${m.systolic}/${m.diastolic} mmHg; ${m.pulse}bpm`;
    };

    const tabStyle = (isActive) => ({
        cursor: "pointer",
        padding: "0.5rem 1.25rem",
        fontFamily: "Poppins, sans-serif",
        fontSize: "14px",
        color: isActive ? "#2c80cf" : "#9B9B9B",
        fontWeight: isActive ? 500 : 400,
        borderBottom: isActive ? "2px solid #2c80cf" : "2px solid transparent",
    });

    return (
        <Container fluid>
            <Row>
                <Col md={12}>
                    <Card className="mt-0 border-0" style={{ position: "relative" }}>
                        <Card.Body>
                            {/* Photo — read-only (no `editable` prop, like Member Details) */}
                            <Avatars initAvatar={memberEditData["account_avatar"]} />
                            <br />

                            {/* PRIMARY BIOMETRICS */}
                            <Card className="p-3 m-3">
                                <Form onSubmit={handleSave}>
                                    <Row>
                                        <h5 className="basicinfo">{primaryBiometrics()}</h5>

                                        <Col md={6}>
                                            <Form.Group controlId="height_cm" className="floating-label-group">
                                                <Form.Control
                                                    type="number"
                                                    name="height_cm"
                                                    value={heightCm}
                                                    onChange={onHeightCm}
                                                    className="floating-input basicinfoinput"
                                                />
                                                <Form.Label className="floating-label basicinfolabel">{height()} (cm)</Form.Label>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group controlId="weight_kg" className="floating-label-group">
                                                <Form.Control
                                                    type="number"
                                                    name="weight_kg"
                                                    value={weightKg}
                                                    onChange={onWeightKg}
                                                    className="floating-input basicinfoinput"
                                                />
                                                <Form.Label className="floating-label basicinfolabel">{weight()} (kg)</Form.Label>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="mt-3">
                                        <Col className="mt-4">
                                            <Button variant="primary" type="submit" disabled={saving || !accountId} className="me-2 instructorphoto">
                                                {savechaneg()}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card>

                            {/* MEASUREMENT LOG */}
                            <Card className="p-3 m-3">
                                {/* Tabs */}
                                <div className="d-flex" style={{ borderBottom: "1px solid #E9E9E9", marginBottom: 16 }}>
                                    <div style={tabStyle(activeTab === DEVICE_OXIMETER)} onClick={() => setActiveTab(DEVICE_OXIMETER)}>
                                        {oxygenSaturation()}
                                    </div>
                                    <div style={tabStyle(activeTab === DEVICE_BLOOD_PRESSURE)} onClick={() => setActiveTab(DEVICE_BLOOD_PRESSURE)}>
                                        {bloodPressure()}
                                    </div>
                                </div>

                                {/* Title + filters */}
                                <div className="d-flex justify-content-between align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
                                    <h5 className="basicinfo mb-0">{recentMeasurementLog()}</h5>
                                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                        <span style={{ color: "#9B9B9B", fontSize: 13 }}>{filterBy()}:</span>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "auto" }}
                                            value={filterMonth}
                                            onChange={(e) => setFilterMonth(Number(e.target.value))}
                                        >
                                            {MONTH_LABEL_KEYS.map((key, idx) => (
                                                <option key={idx} value={idx}>{t[key]()}</option>
                                            ))}
                                        </Form.Select>
                                        <span style={{ color: "#9B9B9B", fontSize: 13 }}>{year()}</span>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "auto" }}
                                            value={filterYear}
                                            onChange={(e) => setFilterYear(Number(e.target.value))}
                                        >
                                            {yearOptions.map((y) => (
                                                <option key={y} value={y}>{y}</option>
                                            ))}
                                        </Form.Select>
                                    </div>
                                </div>

                                <Table bordered={false} responsive>
                                    <thead>
                                        <tr>
                                            <th className="table-header">{date()}</th>
                                            <th className="table-header">{time()}</th>
                                            <th className="table-header">
                                                {activeTab === DEVICE_OXIMETER ? oxygenSatAndPulse() : bloodPressureAndPulse()}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="3" className="text-center">
                                                    <Spinner animation="border" size="sm" />
                                                </td>
                                            </tr>
                                        ) : rows.length === 0 ? (
                                            <tr>
                                                <td style={{ color: "#9B9B9B" }} colSpan="3" className="text-center">
                                                    {noMeasurementData()}
                                                </td>
                                            </tr>
                                        ) : (
                                            rows.map((m) => (
                                                <tr key={m.ocr_record_id}>
                                                    <td>{formatDate(m.measure_timestamp)}</td>
                                                    <td>{formatTime(m.measure_timestamp)}</td>
                                                    <td>{formatReading(m)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </Card>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default HealthProfile;
